import React, { useState } from 'react';
import { Card, Switch, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import api from '../../services/api';
import { User } from '../../types';

const { Text } = Typography;

const SettingRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const SettingCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

interface PreferencesSectionProps {
  user: User;
  onUpdate: (user: User) => void;
}

/**
 * Account-level preferences. Currently just the search scope: the top bar
 * searches the active estate unless this is switched on.
 */
const PreferencesSection: React.FC<PreferencesSectionProps> = ({ user, onUpdate }) => {
  const [saving, setSaving] = useState<boolean>(false);

  const handleToggle = async (checked: boolean) => {
    setSaving(true);
    try {
      const response = await api.patch('/user/me/preferences', { searchAllEstates: checked });
      onUpdate(response.data);
      message.success(
        checked ? 'Search now covers all your estates' : 'Search now covers the active estate only',
      );
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={<><SearchOutlined /> Search</>} style={{ marginBottom: '2rem' }}>
      <SettingRow>
        <SettingCopy>
          <Text strong>Search across all estates</Text>
          <Text type="secondary">
            Assets, liabilities, cashflows, participants and documents are searched only within the
            active estate. Turn this on to search every estate you have access to, with each result
            labelled by the estate it belongs to.
          </Text>
        </SettingCopy>
        <Switch checked={!!user.searchAllEstates} loading={saving} onChange={handleToggle} />
      </SettingRow>
    </Card>
  );
};

export default PreferencesSection;
